// Server synchronization. Startup and retry only read shared state.
// Individual edits use the item endpoints; browser snapshots never replace
// entire collections. Preserve local data when the server returns empty data.
import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import { LEGACY_DEMO_USER_IDS } from "../mock/mock";

const BOOTSTRAP_DONE_KEY = "cb_backend_bootstrap_done";
const LAST_SYNC_KEY = "cb_backend_last_sync";
const DEMO_CLEANUP_KEY = "cb_demo_user_cleanup_v1";

// Which storage key maps to which backend collection path.
const MAP = {
  users: { lsKey: "cb_users_v1", path: "users" },
  products: { lsKey: "cb_products_v1", path: "products" },
  suppliers: { lsKey: "cb_suppliers_v1", path: "suppliers" },
  requests: { lsKey: "cb_requests_v1", path: "requests" },
  categories: { lsKey: "cb_categories_v1", path: "categories" },
};

let listeners = [];
let status = { online: null, syncing: false, error: null, lastSync: null };

function emit() {
  listeners.forEach((l) => l(status));
}
export function subscribeSync(fn) {
  listeners.push(fn);
  fn(status);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}
export function getSyncStatus() { return status; }

function setStatus(patch) {
  status = { ...status, ...patch };
  emit();
}

function readLocal(kind) {
  try { return JSON.parse(localStorage.getItem(MAP[kind].lsKey) || "[]"); } catch (_e) { return []; }
}
function writeLocal(kind, items) {
  localStorage.setItem(MAP[kind].lsKey, JSON.stringify(items));
}

export async function bootstrap() {
  setStatus({ syncing: true, error: null });
  try {
    // One-time scrub: legacy demo employees that used to ship with the
    // app must never reappear on a fresh device or sync back into the
    // workspace. Strip them from local storage AND ask the backend to
    // delete them, then mark cleanup as done so this is a no-op on
    // subsequent loads. Runs before /bootstrap so the merge below cannot
    // re-push them.
    if (localStorage.getItem(DEMO_CLEANUP_KEY) !== "1") {
      try {
        const localUsers = readLocal("users");
        const cleaned = localUsers.filter((u) => !LEGACY_DEMO_USER_IDS.includes(u.id));
        if (cleaned.length !== localUsers.length) writeLocal("users", cleaned);
      } catch (e) {
        console.warn("[sync] legacy demo cleanup skipped — corrupt local users:", e);
      }
      await Promise.allSettled(
        LEGACY_DEMO_USER_IDS.map((id) =>
          apiDelete(`/users/${id}`).catch(() => null)
        )
      );
      localStorage.setItem(DEMO_CLEANUP_KEY, "1");
    }

    const snap = await apiGet("/bootstrap");
    // Validate the entire response before changing any device data.
    // An unexpectedly empty collection must not destroy the last offline copy.
    for (const kind of Object.keys(MAP)) {
      if (!Array.isArray(snap?.[kind])) {
        throw new Error("Invalid server response. The saved offline data has been preserved.");
      }
      if (snap[kind].length === 0 && readLocal(kind).length > 0) {
        throw new Error("The server returned an empty collection. The saved offline data has been preserved; contact the administrator.");
      }
    }
    for (const kind of Object.keys(MAP)) {
      writeLocal(kind, snap[kind]);
    }
    localStorage.setItem(BOOTSTRAP_DONE_KEY, "1");
    const now = new Date().toISOString();
    localStorage.setItem(LAST_SYNC_KEY, now);
    setStatus({ online: true, syncing: false, error: null, lastSync: now });
    return { ok: true };
  } catch (e) {
    setStatus({ online: false, syncing: false, error: String(e?.message || e) });
    return { ok: false, error: e };
  }
}

// Fire-and-forget upsert. Doesn't block the UI.
export function pushItem(kind, item) {
  if (!MAP[kind]) return;
  setStatus({ syncing: true });
  apiPost(`/${MAP[kind].path}`, item)
    .then(() => setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() }))
    .catch((e) => setStatus({ online: false, syncing: false, error: String(e?.message || e) }));
}

export function deleteItem(kind, id) {
  if (!MAP[kind]) return;
  setStatus({ syncing: true });
  apiDelete(`/${MAP[kind].path}/${id}`)
    .then(() => setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() }))
    .catch((e) => {
      // 404 is fine (already gone) — don't mark offline just for that
      const code = e?.response?.status;
      if (code === 404) return setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() });
      setStatus({ online: false, syncing: false, error: String(e?.message || e) });
    });
}

export function patchItem(kind, id, patch) {
  if (!MAP[kind]) return;
  setStatus({ syncing: true });
  apiPatch(`/${MAP[kind].path}/${id}`, patch)
    .then(() => setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() }))
    .catch((e) => setStatus({ online: false, syncing: false, error: String(e?.message || e) }));
}

export function patchRequestItem(requestId, itemId, patch) {
  setStatus({ syncing: true });
  apiPatch(`/requests/${requestId}/items/${itemId}`, patch)
    .then(() => setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() }))
    .catch((e) => setStatus({ online: false, syncing: false, error: String(e?.message || e) }));
}

export function deleteRequestItem(requestId, itemId) {
  setStatus({ syncing: true });
  apiDelete(`/requests/${requestId}/items/${itemId}`)
    .then(() => setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() }))
    .catch((e) => {
      const code = e?.response?.status;
      if (code === 404) return setStatus({ online: true, syncing: false, error: null, lastSync: new Date().toISOString() });
      setStatus({ online: false, syncing: false, error: String(e?.message || e) });
    });
}

export async function forceResync() {
  // Retrying sync only reads the server; it must never upload a full snapshot.
  return bootstrap();
}

export function wasBootstrapped() {
  return localStorage.getItem(BOOTSTRAP_DONE_KEY) === "1";
}
